import { inject, Injectable } from '@angular/core';
import type { BearerToken, ImageLoadState, IrisStatusResponse } from './iris.interface';
import type * as angular from 'angular';
import { IrisClient } from './iris-client';
import { angularjs } from '../migration-helpers/angularjs.injection';

const TOKEN_EXPIRY_BUFFER_MS = 30000;

@Injectable({
  providedIn: 'root'
})
export class IrisStrategyService {
  private tokenCache = new Map<string, BearerToken>();

  private loadPromises = new Map<string, angular.IPromise<BearerToken>>();

  private retryDelayMs: number;

  $q: angular.IQService = inject<angular.IQService>(angularjs.$q);
  $timeout: angular.ITimeoutService = inject<angular.ITimeoutService>(angularjs.$timeout);
  irisClient: IrisClient = inject<IrisClient>(IrisClient);

  constructor() {
    this.retryDelayMs = window.irisStatusCallWaitTime ?? 1000;
  }

  isTokenAvailable(bearerTokenURL: string): boolean {
    const cached = this.tokenCache.get(bearerTokenURL);
    if (!cached) return false;
    const expiration = new Date(cached.expirationDateTime).getTime();
    return expiration - TOKEN_EXPIRY_BUFFER_MS > Date.now();
  }

  getToken(bearerTokenURL: string): string {
    const cached = this.tokenCache.get(bearerTokenURL);
    if (!cached) throw new Error(`No cached token for ${bearerTokenURL}`);
    return cached.token;
  }

  refresh(bearerTokenURL: string): angular.IPromise<BearerToken> {
    const inflight = this.loadPromises.get(bearerTokenURL);
    if (inflight) return inflight;

    const promise = this.$q
      .when(this.irisClient.getBearerToken(bearerTokenURL))
      .then((tokenRes) => {
        this.tokenCache.set(bearerTokenURL, tokenRes.data);
        return tokenRes.data;
      })
      .finally(() => {
        this.loadPromises.delete(bearerTokenURL);
      });

    this.loadPromises.set(bearerTokenURL, promise);
    return promise;
  }

  shouldRetry(state: ImageLoadState, maxRetries: number): boolean {
    return state.attempts < maxRetries;
  }

  scheduleRetry(
    state: ImageLoadState,
    retryFn: () => angular.IPromise<string> | Promise<string>
  ): angular.IPromise<string> {
    state.attempts++;
    return this.$timeout(retryFn, this.retryDelayMs);
  }

  async fetchStatus(url: string, bearerTokenURL: string): Promise<IrisStatusResponse> {
    if (!this.isTokenAvailable(bearerTokenURL)) await this.refresh(bearerTokenURL);

    try {
      const response = await this.irisClient.getStatus(url, this.getToken(bearerTokenURL));
      return response.data;
    } catch (error) {
      const err = error as { status: number };
      if (err.status === 401) {
        this.tokenCache.delete(bearerTokenURL);
        await this.refresh(bearerTokenURL);
        const retryResponse = await this.irisClient.getStatus(url, this.getToken(bearerTokenURL));
        return retryResponse.data;
      }
      throw error;
    }
  }
}
